import { useEffect, useState } from "react";
import PROFILE from "../api/services/PROFILE";

/**
 * Hook that returns Hebrew terminology for "appointments" vs. "classes",
 * keyed off the business's `businessType` field on the profile.
 *
 *   appointment-style (default): "תור" / "תורים"
 *   class-style:                  "שיעור" / "שיעורים"
 *
 * Class-style business types: 'fitness', 'wellness', 'sports'.
 *
 * The hook caches the businessType in localStorage so subsequent mounts
 * resolve synchronously without a network round-trip. If the profile
 * endpoint doesn't return `businessType` yet, the hook silently falls
 * back to appointment-style wording (`isClassBusiness: false`). To turn
 * on the class-style path for a business, the backend's GET_PROFILE
 * response simply needs to include a `businessType` field with one of
 * the values listed above.
 */

const CLASS_BUSINESS_TYPES = new Set(["fitness", "wellness", "sports"]);
const STORAGE_KEY = "business_type";

const APPOINTMENT_TERMS = {
  single: "תור",
  plural: "תורים",
  theSingle: "התור",
  thePlural: "התורים",
  next: "התור הבא",
  todayLabel: "תורים היום",
  monthLabel: "תורים החודש",
  totalLabel: "סה”כ תורים",
  emptyToday: "לא נקבעו תורים היום",
};

const CLASS_TERMS = {
  single: "שיעור",
  plural: "שיעורים",
  theSingle: "השיעור",
  thePlural: "השיעורים",
  next: "השיעור הבא",
  todayLabel: "שיעורים היום",
  monthLabel: "שיעורים החודש",
  totalLabel: "סה”כ שיעורים",
  emptyToday: "לא נקבעו שיעורים היום",
};

const isClassType = (t) =>
  !!t && CLASS_BUSINESS_TYPES.has(String(t).toLowerCase());

const buildTerms = (businessType) => {
  const isClassBusiness = isClassType(businessType);
  const base = isClassBusiness ? CLASS_TERMS : APPOINTMENT_TERMS;
  return { ...base, isClassBusiness };
};

const readCached = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    // Tolerate either raw string or JSON-encoded string
    if (raw.startsWith("\"")) return JSON.parse(raw);
    return raw;
  } catch (_) {
    return null;
  }
};

const writeCached = (val) => {
  try {
    if (val) localStorage.setItem(STORAGE_KEY, val);
  } catch (_) {}
};

export const useAppointmentTerminology = () => {
  const [businessType, setBusinessType] = useState(() => readCached());

  useEffect(() => {
    let cancelled = false;
    const fetchType = async () => {
      try {
        const res = await PROFILE.get();
        const t = res?.data?.businessType || res?.data?.business_type || null;
        if (cancelled) return;
        if (t) {
          writeCached(t);
          setBusinessType(t);
        }
      } catch (_) {
        // Silent fallback: keep cached value (or null → appointment-style).
      }
    };

    // Only fetch if we don't have a cached value yet — the businessType
    // changes rarely, and other screens (settings/header) re-hydrate it.
    if (!businessType) fetchType();
    return () => {
      cancelled = true;
    };
  }, [businessType]);

  return buildTerms(businessType);
};

export default useAppointmentTerminology;
